// Idea bank: content_log rows with status='idea', ranked by priority.
// Quick add parks a new idea; Edit/Delete manage the backlog; Promote
// drops one into a quarter + week (POST /content/:id/promote) and it
// appears on the Plan and the board.

import { useMemo, useState } from 'react';
import { useContent } from '../../../context/ContentContext';
import { apiFetch } from '../../../utils/apiFetch';
import { PILLARS, getPillarLabel } from '../pillars';
import { CHANNELS, PRIORITIES, quarterOf, nextQuarterOf, mondaysOfQuarter, toDateKey } from '../stages';
import { Card, Eyebrow, Pill } from '../ui';

const PRIORITY_TONE = { high: 'crit', med: 'neutral', low: 'neutral' };
const PRIORITY_RANK = { high: 0, med: 1, low: 2 };

export default function IdeasView() {
  const { entries, refresh, removeOne } = useContent();
  const [showAdd, setShowAdd] = useState(false);
  const [promoting, setPromoting] = useState(null); // entry being promoted
  const [editing, setEditing] = useState(null);     // entry being edited
  const [err, setErr] = useState(null);

  async function handleDelete(idea) {
    if (!window.confirm(`Delete idea "${idea.title}"? This cannot be undone.`)) return;
    setErr(null);
    try {
      await removeOne(idea.id);
    } catch (e) {
      setErr(e.message);
    }
  }

  const ideas = useMemo(
    () => entries
      .filter(e => e.status === 'idea')
      .sort((a, b) =>
        (PRIORITY_RANK[a.priority] ?? 3) - (PRIORITY_RANK[b.priority] ?? 3)
        || (a.title || '').localeCompare(b.title || '')),
    [entries]
  );

  return (
    <div className="flex flex-col gap-3">
      <Card lash="blue">
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-line">
          <div>
            <Eyebrow>Idea bank</Eyebrow>
            <h3 className="text-navy text-[16px] font-black mt-[3px]">
              {ideas.length} {ideas.length === 1 ? 'topic' : 'topics'} · parked and prioritized
            </h3>
          </div>
          <button
            onClick={() => setShowAdd(v => !v)}
            className="text-bullogic-blue text-[11px] font-black border border-bullogic-blue/40 rounded-[6px] px-3 py-1.5 hover:bg-bullogic-blue/5"
          >
            + Quick add
          </button>
        </div>

        {showAdd && <QuickAddRow onDone={() => setShowAdd(false)} />}

        {ideas.length === 0 && !showAdd && (
          <div className="px-4 py-5 text-[12.5px] text-ink-2">
            No parked ideas yet. Quick add drops topics here; Promote moves one into a quarter when its time comes.
          </div>
        )}

        <div>
          {ideas.map(idea => (
            <div key={idea.id} className="grid grid-cols-[1fr_auto] gap-3 items-center px-4 py-2.5 border-b border-line-2 last:border-b-0 hover:bg-[#faf9f6]">
              <div>
                <div className="font-semibold text-[13px] text-ink">{idea.title}</div>
                <div className="text-ink-2 text-[11.5px] mt-0.5">
                  {[getPillarLabel(idea.pillar), idea.channel === 'youtube' ? 'YouTube' : null, idea.angle]
                    .filter(Boolean).join(' · ')}
                </div>
              </div>
              <div className="flex gap-2 items-center">
                {idea.priority && (
                  <Pill tone={PRIORITY_TONE[idea.priority] || 'neutral'} className="text-[10px]">
                    {PRIORITIES.find(p => p.id === idea.priority)?.label || idea.priority}
                  </Pill>
                )}
                <button
                  onClick={() => setEditing(idea)}
                  className="text-ink-2 text-[11px] font-bold border border-line rounded-[6px] px-2.5 py-1 hover:text-navy hover:border-ink-3"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(idea)}
                  title="Delete idea"
                  className="text-rose/70 text-[11px] font-bold border border-line rounded-[6px] px-2 py-1 hover:text-rose hover:border-rose/40"
                >
                  ✕
                </button>
                <button
                  onClick={() => setPromoting(idea)}
                  className="text-bullogic-blue text-[11px] font-black border border-bullogic-blue/40 rounded-[6px] px-2.5 py-1 hover:bg-bullogic-blue/5"
                >
                  Promote →
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {err && <div className="text-[12px] text-rose">{err}</div>}

      {promoting && (
        <PromoteModal
          idea={promoting}
          onClose={() => setPromoting(null)}
          onPromoted={async () => { setPromoting(null); await refresh(); }}
          onError={setErr}
        />
      )}

      {editing && (
        <IdeaEditModal
          idea={editing}
          onClose={() => setEditing(null)}
          onError={setErr}
        />
      )}
    </div>
  );
}

function IdeaEditModal({ idea, onClose, onError }) {
  const { saveOne } = useContent();
  const [title, setTitle] = useState(idea.title || '');
  const [pillar, setPillar] = useState(idea.pillar || PILLARS[0].id);
  const [channel, setChannel] = useState(idea.channel || 'blog');
  const [priority, setPriority] = useState(idea.priority || 'med');
  const [angle, setAngle] = useState(idea.angle || '');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await saveOne({ ...idea, title: title.trim(), pillar, channel, priority, angle: angle.trim() || null });
      onClose();
    } catch (e) {
      onError(e.message);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const input = 'w-full border border-line rounded-[6px] px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:border-bullogic-blue';
  return (
    <div className="fixed inset-0 z-50 bg-navy/20 grid place-items-center" onClick={onClose}>
      <div className="bg-white border border-line rounded-[10px] shadow-[0_8px_24px_rgba(2,10,40,0.10)] p-4 w-[440px]" onClick={e => e.stopPropagation()}>
        <div className="font-black text-navy text-[14px] mb-3">Edit idea</div>
        <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Title</label>
        <input autoFocus value={title} onChange={e => setTitle(e.target.value)} className={input} />
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Pillar</label>
            <select value={pillar} onChange={e => setPillar(e.target.value)} className={input}>
              {PILLARS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Channel</label>
            <select value={channel} onChange={e => setChannel(e.target.value)} className={input}>
              {CHANNELS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Priority</label>
            <select value={priority} onChange={e => setPriority(e.target.value)} className={input}>
              {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
        </div>
        <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1 mt-3">Angle</label>
        <input value={angle} onChange={e => setAngle(e.target.value)} className={input} />
        <div className="flex justify-end gap-2 mt-3.5">
          <button onClick={onClose} className="text-[12px] font-bold text-ink-3 px-3 py-1.5">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !title.trim()}
            className="bg-deep-teal text-white text-[12px] font-black rounded-[6px] px-3.5 py-1.5 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save idea'}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuickAddRow({ onDone }) {
  const { saveOne } = useContent();
  const [title, setTitle] = useState('');
  const [pillar, setPillar] = useState(PILLARS[0].id);
  const [channel, setChannel] = useState('blog');
  const [priority, setPriority] = useState('med');
  const [angle, setAngle] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      await saveOne({ title: title.trim(), pillar, channel, priority, angle: angle.trim() || null, status: 'idea' });
      onDone();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  }

  const input = 'border border-line rounded-[6px] px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:border-bullogic-blue';
  return (
    <form onSubmit={submit} className="px-4 py-3 border-b border-line bg-[#faf9f6] flex flex-wrap gap-2 items-center">
      <input autoFocus value={title} onChange={e => setTitle(e.target.value)} placeholder="Idea title" className={`${input} flex-1 min-w-[220px]`} />
      <select value={pillar} onChange={e => setPillar(e.target.value)} className={input}>
        {PILLARS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
      </select>
      <select value={channel} onChange={e => setChannel(e.target.value)} className={input}>
        {CHANNELS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
      <select value={priority} onChange={e => setPriority(e.target.value)} className={input}>
        {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
      </select>
      <input value={angle} onChange={e => setAngle(e.target.value)} placeholder="Angle (optional)" className={`${input} flex-1 min-w-[180px]`} />
      <button
        type="submit"
        disabled={saving || !title.trim()}
        className="bg-deep-teal text-white text-[12px] font-black rounded-[6px] px-3.5 py-1.5 disabled:opacity-50"
      >
        {saving ? 'Adding…' : 'Add idea'}
      </button>
      {err && <span className="text-[11px] text-rose">{err}</span>}
    </form>
  );
}

function PromoteModal({ idea, onClose, onPromoted, onError }) {
  const current = quarterOf();
  const next = nextQuarterOf();
  const [quarter, setQuarter] = useState(current);
  const [week, setWeek] = useState('');
  const [saving, setSaving] = useState(false);

  const mondays = useMemo(() => mondaysOfQuarter(quarter), [quarter]);
  const today = new Date(); today.setHours(0, 0, 0, 0);

  async function promote() {
    setSaving(true);
    try {
      const res = await apiFetch(`/api/data/content/${idea.id}/promote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quarter, planWeek: week || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await onPromoted();
    } catch (e) {
      onError(e.message);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-navy/20 grid place-items-center" onClick={onClose}>
      <div className="bg-white border border-line rounded-[10px] shadow-[0_8px_24px_rgba(2,10,40,0.10)] p-4 w-[400px]" onClick={e => e.stopPropagation()}>
        <div className="font-black text-navy text-[13.5px]">Promote "{idea.title}"</div>
        <div className="text-ink-2 text-[12px] mt-1 mb-3">Pick the quarter and (optionally) the week it should ship.</div>

        <div className="flex gap-2 mb-2.5">
          {[current, next].map(q => (
            <button
              key={q}
              onClick={() => { setQuarter(q); setWeek(''); }}
              className={`text-[12px] font-bold px-3 py-1.5 rounded-[6px] border ${
                quarter === q ? 'bg-navy text-white border-navy' : 'border-line text-ink-2 hover:text-navy'
              }`}
            >
              {q}
            </button>
          ))}
        </div>

        <select
          value={week}
          onChange={e => setWeek(e.target.value)}
          className="w-full border border-line rounded-[6px] px-2.5 py-1.5 text-[12.5px] mb-3 focus:outline-none focus:border-bullogic-blue"
        >
          <option value="">No week yet (assign later)</option>
          {mondays.filter(m => m >= today || quarter !== quarterOf()).map(m => (
            <option key={toDateKey(m)} value={toDateKey(m)}>
              Week of {m.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </option>
          ))}
        </select>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-[12px] font-bold text-ink-3 px-3 py-1.5">Cancel</button>
          <button
            onClick={promote}
            disabled={saving}
            className="bg-deep-teal text-white text-[12px] font-black rounded-[6px] px-3.5 py-1.5 disabled:opacity-50"
          >
            {saving ? 'Promoting…' : `Promote to ${quarter}`}
          </button>
        </div>
      </div>
    </div>
  );
}
